import React, {FormEvent, ReactElement, useState} from 'react';
import {changePassword, gonative_save_secret, isBiometryActivated, logout, passwordChanged} from '~/entities/user';
import {ServerValidationResult, useHttpClient, ValidationFields} from '~/library/network-facade';
import {RootLayout, RsnBadge, RsnButton, RsnDialog, RsnInput, useBoolean} from '~/library/ui-kit';
import {AllowedSymbolsDialog} from '~/pages/old-onboarding/ui/AllowedSymbolsDialog';
import {useAppDispatch} from '~/shared/store';
import {getSavePassHandler} from '~/shared/utils/savePass';
import {Password} from '../../model/Password';

const savePassHandler = getSavePassHandler();

type FormData = {
    old_password: string;
    new_password: {
        first: string;
        second: string;
    };
};

function ChangePasswordPage() {
    const oldPasswordHelper = useFeedbackHelper();
    const newPasswordHelper = useFeedbackHelper();
    const repeatPasswordHelper = useFeedbackHelper();

    const [isLoading, setIsLoading] = useState(false);
    const [serverErrors, setServerErrors] = useState<ServerValidationResult<ValidationFields<FormData>>>();
    const dispatch = useAppDispatch();
    const http = useHttpClient();

    const [dialog, {on: open, off: close}] = useBoolean(false);

    const [oldPasswordInput, setOldPasswordInput] = useState('');
    const onOldPasswordChange: React.ChangeEventHandler<HTMLInputElement> = event => {
        oldPasswordHelper.signalInteraction();
        setServerErrors(undefined);
        setOldPasswordInput(event.target.value);
    };

    const [newPasswordInput, setNewPasswordInput] = useState('');
    const onNewPasswordInputChange: React.ChangeEventHandler<HTMLInputElement> = event => {
        newPasswordHelper.signalInteraction();
        setServerErrors(undefined);
        setNewPasswordInput(event.target.value);
    };

    const [repeatPasswordInput, setRepeatPasswordInput] = useState('');
    const onMatchChange: React.ChangeEventHandler<HTMLInputElement> = event => {
        repeatPasswordHelper.signalInteraction();
        setServerErrors(undefined);
        setRepeatPasswordInput(event.target.value);
    };

    const {passwordsDoMatch, passwordIsValid, validations, ...errors} = Password.validatePasswords(
        newPasswordInput,
        repeatPasswordInput
    );

    const formIsValid = passwordsDoMatch && passwordIsValid && !!oldPasswordInput;

    const handleSubmit = async () => {
        oldPasswordHelper.signalInteraction();
        newPasswordHelper.signalInteraction();
        repeatPasswordHelper.signalInteraction();

        if (!formIsValid) return;

        setIsLoading(true);

        const data = {
            old_password: oldPasswordInput,
            new_password: {
                first: newPasswordInput,
                second: repeatPasswordInput,
            },
        };

        const {status, json} = await changePassword(http, data).finally(() => setIsLoading(false));

        if (status === 401) {
            dispatch(logout());
            return;
        }

        if (status === 400 || status === 422) {
            setServerErrors(json);
            return;
        }

        if (isBiometryActivated()) {
            savePassHandler.setPassword(newPasswordInput);
            gonative_save_secret();
            setTimeout(() => {
                dispatch(passwordChanged());
            }, 1000);
        } else {
            dispatch(passwordChanged());
        }
    };

    return (
        <RootLayout title='Change password'>
            <form
                onSubmit={(e: FormEvent) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSubmit();
                }}
                className={`flex flex-1 flex-col gap-6 px-4 pb-4 pt-6`}
            >
                <div className='flex flex-col gap-4'>
                    <small className='fv-text-l-regular text-neutral-90'>
                        New password must match the following requirements:
                    </small>

                    <div className='flex flex-wrap gap-2'>
                        {validations.map((criteria, i) => (
                            <RsnBadge.Md key={i} color={criteria.isMet ? 'success' : 'neutral'}>
                                {criteria.name}
                            </RsnBadge.Md>
                        ))}
                    </div>
                </div>

                <RsnInput.Password
                    {...oldPasswordHelper.getHelperProps(
                        required(oldPasswordInput) || serverErrors?.fields?.old_password?.[0]
                    )}
                    label='Current password'
                    onChange={onOldPasswordChange}
                    value={oldPasswordInput}
                />

                <RsnInput.Password
                    {...newPasswordHelper.getHelperProps(
                        errors.getMainError(open) || serverErrors?.fields?.new_password?.first?.[0]
                    )}
                    label='New password'
                    onChange={onNewPasswordInputChange}
                    value={newPasswordInput}
                />

                <RsnInput.Password
                    {...repeatPasswordHelper.getHelperProps(
                        errors.getMatchError() || serverErrors?.fields?.new_password?.second?.[0]
                    )}
                    label='Confirm password'
                    onChange={onMatchChange}
                    value={repeatPasswordInput}
                />

                <div className='mt-auto'>
                    <RsnButton.Primary disabled={isLoading} type='submit'>
                        Save password
                    </RsnButton.Primary>
                </div>
            </form>
            <RsnDialog.Controller isOpen={dialog} onClose={close}>
                <AllowedSymbolsDialog />
            </RsnDialog.Controller>
        </RootLayout>
    );
}

export default ChangePasswordPage;

const useFeedbackHelper = () => {
    const [hasInteracted, setHasInteracted] = useState(false);
    const signalInteraction = () => {
        if (!hasInteracted) setHasInteracted(true);
    };
    const getHelperProps = (error: string | ReactElement | undefined) =>
        hasInteracted && {
            helperText: error,
            isInvalid: !!error,
        };
    return {signalInteraction, getHelperProps};
};

const required = (value: string) => (!value ? 'This field is required' : undefined);
